import { DataSource } from '@client/core/data/concerns/data-source';
import { fromJson, JsonResponse } from '@client/core/data/concerns/parses-from-json';
import { HttpResponse } from '@client/core/http/types';
import { snakeCaseKeys } from '@client/core/utils/snake-case-keys';
import { Project } from '@client/features/project/models/project';

export interface UserCanDestroyProject {
  hasFeatureAccess: boolean;
  hasProjectAccess: boolean;
}

export class ProjectDataSource extends DataSource {
  /**
   * @see Api\ProjectController::update
   */
  async update(projectId: Project['uuid'], data: Partial<Project>): Promise<Project> {
    const requestData = snakeCaseKeys(data);

    const response = await this.httpClient.patch<HttpResponse<JsonResponse>>(`/projects/${projectId}`, requestData);

    // this is a legacy API, meaning it does not use a JSON resource,
    // so the response is at response.data, not response.data.data
    return fromJson(Project, response.data);
  }

  /**
   * See Api\ProjectController::updateSettings
   */
  async updateStatus(projectId: Project['uuid'], status: Project['status']): Promise<void> {
    await this.httpClient.patch<HttpResponse<void>>(`/project/${projectId}/settings`, { status });
  }

  /**
   * See Api\ProjectController::destroy
   */
  async destroy(projectId: Project['uuid']): Promise<void> {
    await this.httpClient.delete<HttpResponse<void>>(`/project/${projectId}`);
  }

  /**
   * Whether logged-in User can delete the given Project.
   */
  async destroyCheck(projectUuid: Project['uuid']): Promise<UserCanDestroyProject> {
    const uri = `/project/${projectUuid}/destroy-check`;
    const response = await this.httpClient.get<HttpResponse<UserCanDestroyProject>>(uri);

    return response.data.data;
  }
}
